import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInAnonymously as firebaseSignInAnonymously,
  GoogleAuthProvider,
  signInWithPopup,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  isSignInWithEmailLink,
  sendPasswordResetEmail,
  signOut as firebaseSignOut,
  UserCredential,
  User as FirebaseUser,
} from 'firebase/auth'

import { getAppBaseUrl } from '../_utilities/utilities'

const MAGIC_LINK_PENDING_EMAIL_STORAGE_KEY =
  'quickpass:pending-magic-link-email'

const buildActionCodeSettings = (): {
  url: string
  handleCodeInApp: boolean
} => {
  return {
    url: `${getAppBaseUrl()}/#/magic-link-callback`,
    handleCodeInApp: true,
  }
}

/**
 * Signs in with email + password. Wrapper isolates Firebase Auth from the
 * AuthContext and components.
 *
 * @param {string} email
 * @param {string} password
 * @returns {Promise<UserCredential>}
 */
const signInWithEmailPassword = async (
  email: string,
  password: string,
): Promise<UserCredential> => {
  const auth = getAuth()

  return await signInWithEmailAndPassword(auth, email, password)
}

/**
 * Creates a new account with email + password.
 *
 * @param {string} email
 * @param {string} password
 * @returns {Promise<UserCredential>}
 */
const signUpWithEmailPassword = async (
  email: string,
  password: string,
): Promise<UserCredential> => {
  const auth = getAuth()

  return await createUserWithEmailAndPassword(auth, email, password)
}

/**
 * Signs the renter in anonymously, minting a real (anonymous) Firebase Auth
 * UID. Used by the cross-site inquiry flow so a no-account renter still gets
 * a durable identity — and can later be promoted to a full account in place
 * via `linkWithCredential` with no data migration.
 *
 * @returns {Promise<UserCredential>}
 */
const signInAnonymously = async (): Promise<UserCredential> => {
  const auth = getAuth()

  return await firebaseSignInAnonymously(auth)
}

/**
 * Opens a Google OAuth popup. Capacitor wrap will need to swap this for
 * `signInWithRedirect` later — popup behaviour differs on native shells.
 *
 * @returns {Promise<UserCredential>}
 */
const signInWithGooglePopup = async (): Promise<UserCredential> => {
  const auth = getAuth()
  const provider = new GoogleAuthProvider()

  return await signInWithPopup(auth, provider)
}

/**
 * Sends a passwordless sign-in email with a link that returns the user to
 * the magic-link-callback page. Stashes the email in localStorage so the
 * callback can complete the flow without re-asking the user.
 *
 * @param {string} email
 * @returns {Promise<void>}
 */
const sendMagicLinkEmail = async (email: string): Promise<void> => {
  const auth = getAuth()

  await sendSignInLinkToEmail(auth, email, buildActionCodeSettings())

  window.localStorage.setItem(MAGIC_LINK_PENDING_EMAIL_STORAGE_KEY, email)
}

/**
 * Completes a magic-link sign-in. Reads the pending email from localStorage
 * (set by `sendMagicLinkEmail`); if that's absent (different device, cleared
 * storage), the caller must supply a fallback email entered by the user.
 *
 * @param {string} currentUrl - the current full URL, including the magic-link query string
 * @param {string | null} fallbackEmail - email entered manually if the pending email is missing
 * @returns {Promise<UserCredential | null>} credential, or null if the URL isn't a magic link
 */
const completeMagicLinkSignIn = async (
  currentUrl: string,
  fallbackEmail: string | null,
): Promise<UserCredential | null> => {
  const auth = getAuth()

  if (!isSignInWithEmailLink(auth, currentUrl)) {
    return null
  }

  const stashedEmail = window.localStorage.getItem(
    MAGIC_LINK_PENDING_EMAIL_STORAGE_KEY,
  )
  const email = stashedEmail ?? fallbackEmail

  if (email === null || email === '') {
    throw new Error(
      'Magic-link email is missing — caller must supply it as a fallback when localStorage was cleared.',
    )
  }

  const credential = await signInWithEmailLink(auth, email, currentUrl)

  window.localStorage.removeItem(MAGIC_LINK_PENDING_EMAIL_STORAGE_KEY)

  return credential
}

/**
 * Sends a password-reset email.
 *
 * @param {string} email
 * @returns {Promise<void>}
 */
const sendPasswordReset = async (email: string): Promise<void> => {
  const auth = getAuth()

  await sendPasswordResetEmail(auth, email)
}

/**
 * Signs the current user out.
 *
 * @returns {Promise<void>}
 */
const signOut = async (): Promise<void> => {
  const auth = getAuth()

  await firebaseSignOut(auth)
}

/**
 * Returns the current Firebase user synchronously. Returns null if no user
 * is signed in, or if Firebase Auth hasn't fully initialised yet — prefer
 * the AuthContext's reactive `firebaseUser` for component code.
 *
 * @returns {FirebaseUser | null}
 */
const getCurrentFirebaseUser = (): FirebaseUser | null => {
  const auth = getAuth()

  return auth.currentUser
}

export {
  signInWithEmailPassword,
  signUpWithEmailPassword,
  signInAnonymously,
  signInWithGooglePopup,
  sendMagicLinkEmail,
  completeMagicLinkSignIn,
  sendPasswordReset,
  signOut,
  getCurrentFirebaseUser,
}
